import { supabase } from './supabase';
import { AuthStore } from '../core/store/authStore';
import { EventBus } from '../core/events/EventBus';
import { ToastManager } from '../ui/components/Toast';

export interface Duel {
  id: number;
  challenger_id: string;
  opponent_id: string;
  game_id: string;
  status: 'pending' | 'playing' | 'completed' | 'rejected' | 'cancelled';
  challenger_score: number;
  opponent_score: number;
  winner_id?: string;
}

export class DuelService {
  private static channel: ReturnType<typeof supabase.channel> | null = null;
  public static activeDuel: Duel | null = null;
  public static opponentScore: number = 0;
  public static opponentFinished: boolean = false;

  static init() {
    const user = AuthStore.getUser();
    if (!user) return;

    if (this.channel) return;

    // Listen to broadcast events (invites, accepts, live scores)
    this.channel = supabase.channel('duel-system')
      .on('broadcast', { event: 'duel-invite' }, (payload) => {
        if (payload.payload.opponentId === user.id) {
          EventBus.emit('duel:invite_received', payload.payload);
        }
      })
      .on('broadcast', { event: 'duel-accepted' }, (payload) => {
        if (payload.payload.challengerId === user.id) {
          this.activeDuel = payload.payload.duel;
          this.opponentScore = 0;
          this.opponentFinished = false;
          ToastManager.show('⚔️ Düello kabul edildi! Başlıyor...', 'success');
          if (this.activeDuel) {
            EventBus.emit('duel:start', this.activeDuel);
          }
        }
      })
      .on('broadcast', { event: 'duel-rejected' }, (payload) => {
        if (payload.payload.challengerId === user.id) {
          ToastManager.show('Rakip düelloyu reddetti.', 'warning');
        }
      })
      .on('broadcast', { event: 'duel-score-update' }, (payload) => {
        if (this.activeDuel && payload.payload.duelId === this.activeDuel.id) {
          if (payload.payload.userId !== user.id) {
            this.opponentScore = payload.payload.score;
            EventBus.emit('duel:opponent_score', { score: this.opponentScore });
          }
        }
      })
      .on('broadcast', { event: 'duel-finished' }, (payload) => {
        if (this.activeDuel && payload.payload.duelId === this.activeDuel.id) {
          this.opponentScore = payload.payload.score;
          this.opponentFinished = true;
          EventBus.emit('duel:finished_by_opponent', payload.payload);
        }
      })
      .on('broadcast', { event: 'duel-congrats' }, (payload) => {
        if (payload.payload.toUserId === user.id) {
          // Play a nice sound
          import('../engine/audio/AudioManager').then(({ AudioManager }) => AudioManager.playScoreSound());
          
          // Show the CSS animation on screen
          this.showCongratsAnimation(payload.payload.fromUsername);
        }
      })
      .subscribe((status) => { });
  }

  static disconnect() {
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
    this.activeDuel = null;
  }

  // --- ACTIONS ---

  static async sendInvite(opponentId: string, gameId: string) {
    const user = AuthStore.getUser();
    if (!user || !this.channel) return;

    // 1. Create DB record
    const { data: duel, error } = await supabase
      .from('duels')
      .insert({
        challenger_id: user.id,
        opponent_id: opponentId,
        game_id: gameId,
        status: 'pending'
      })
      .select('*')
      .single();

    if (error || !duel) {
      ToastManager.show('Düello oluşturulamadı.', 'error');
      return;
    }

    // 2. Broadcast invite
    await this.channel.send({
      type: 'broadcast',
      event: 'duel-invite',
      payload: {
        duel,
        opponentId,
        challengerName: user.username
      }
    });

    ToastManager.show('Davet gönderildi. Bekleniyor...', 'info');
  }

  static async acceptInvite(duel: Duel) {
    const user = AuthStore.getUser();
    if (!user || !this.channel) return;

    // 1. Update DB
    await supabase.from('duels').update({ status: 'playing' }).eq('id', duel.id);

    // 2. Broadcast accept
    await this.channel.send({
      type: 'broadcast',
      event: 'duel-accepted',
      payload: {
        duel,
        challengerId: duel.challenger_id
      }
    });

    this.activeDuel = duel;
    this.opponentScore = 0;
    this.opponentFinished = false;
    EventBus.emit('duel:start', duel);
  }

  static async rejectInvite(duel: Duel) {
    const user = AuthStore.getUser();
    if (!user || !this.channel) return;

    await supabase.from('duels').update({ status: 'rejected' }).eq('id', duel.id);

    await this.channel.send({
      type: 'broadcast',
      event: 'duel-rejected',
      payload: { challengerId: duel.challenger_id }
    });
  }

  static async sendLiveScore(score: number) {
    if (!this.channel || !this.activeDuel) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'duel-score-update',
      payload: { duelId: this.activeDuel.id, score, userId: AuthStore.getUser()?.id }
    });
  }

  static async sendDuelFinished(score: number) {
    if (!this.channel || !this.activeDuel) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'duel-finished',
      payload: { duelId: this.activeDuel.id, score }
    });
  }

  static async sendCongrats(toUserId: string) {
    if (!this.channel) return;
    const user = AuthStore.getUser();
    await this.channel.send({
      type: 'broadcast',
      event: 'duel-congrats',
      payload: { toUserId, fromUsername: user?.username || 'Rakip' }
    });
  }

  private static showCongratsAnimation(fromUsername: string) {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.bottom = '20px';
    container.style.right = '20px';
    container.style.zIndex = '9999999';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'center';
    container.style.pointerEvents = 'none';

    container.innerHTML = `
      <div style="font-size: 3rem; animation: floatUp 2s ease-out forwards; text-shadow: 0 4px 10px rgba(0,0,0,0.5);">👏</div>
      <div style="font-size: 3rem; animation: floatUp 2s ease-out 0.2s forwards; text-shadow: 0 4px 10px rgba(0,0,0,0.5); position:absolute;">💖</div>
      <div style="font-size: 3rem; animation: floatUp 2s ease-out 0.4s forwards; text-shadow: 0 4px 10px rgba(0,0,0,0.5); position:absolute;">👏</div>
      <div style="background: rgba(15,23,42,0.9); border: 2px solid #ca8a04; color: #fde047; padding: 8px 16px; border-radius: 20px; font-weight: 800; font-size: 0.9rem; margin-top: 10px; animation: popIn 0.3s ease-out;">
        ${fromUsername} seni tebrik etti!
      </div>
      <style>
        @keyframes floatUp {
          0% { transform: translateY(0) scale(0.5); opacity: 0; }
          20% { transform: translateY(-20px) scale(1.2); opacity: 1; }
          80% { transform: translateY(-80px) scale(1); opacity: 1; }
          100% { transform: translateY(-100px) scale(1); opacity: 0; }
        }
        @keyframes popIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(container);
    setTimeout(() => container.remove(), 4000);
  }
}
